import { useState, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import api, { apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge } from "@/components/common";
import { LinkDialog } from "@/components/LinkDialog";
import { DatePicker } from "@/components/pickers";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UserPlus, Upload, Download, FileSpreadsheet } from "lucide-react";
import { StateZoneFields } from "@/components/StateZoneFields";
import { toast } from "sonner";

const STATUTORY = [
  { v: "ask", l: "Ask employee" },
  { v: "with_statutory", l: "With statutory" },
  { v: "without_statutory", l: "Without statutory" },
];

export default function InviteEmployees() {
  const { actingOrgId } = useAuth();
  return (
    <div>
      <PageHeader title="Invite employees" description="Invite one at a time, or upload a spreadsheet" />
      <Tabs defaultValue="single">
        <TabsList className="mb-5">
          <TabsTrigger value="single" data-testid="tab-single">Single invite</TabsTrigger>
          <TabsTrigger value="bulk" data-testid="tab-bulk">Bulk invite</TabsTrigger>
        </TabsList>
        <TabsContent value="single"><SingleInvite key={actingOrgId} /></TabsContent>
        <TabsContent value="bulk"><BulkInvite key={actingOrgId} /></TabsContent>
      </Tabs>
    </div>
  );
}

function SingleInvite() {
  const { actingOrgId } = useAuth();
  const [form, setForm] = useState({
    name: "", email: "", mobile: "", employee_code: "",
    department_id: "", designation_id: "", location_id: "", date_of_joining: "", statutory_mode: "ask", state_code: "", zone_id: "",
  });
  const [link, setLink] = useState("");

  const { data: depts = [] } = useQuery({ queryKey: ["depts", actingOrgId], queryFn: async () => (await api.get("/org/masters/departments")).data });
  const { data: desigs = [] } = useQuery({ queryKey: ["desigs", actingOrgId], queryFn: async () => (await api.get("/org/masters/designations")).data });
  const { data: locs = [] } = useQuery({ queryKey: ["locs", actingOrgId], queryFn: async () => (await api.get("/org/masters/locations")).data });

  const invite = useMutation({
    mutationFn: async () => (await api.post("/org/employees/invite", form)).data,
    onSuccess: (d) => {
      toast.success(d.sent_status === "sent" ? "Invite emailed" : "Employee added (email not sent)");
      setLink(d.link);
      setForm({ name: "", email: "", mobile: "", employee_code: "", department_id: "", designation_id: "", location_id: "", date_of_joining: "", statutory_mode: "ask", state_code: "", zone_id: "" });
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const sel = (k) => (v) => setForm({ ...form, [k]: v });

  return (
    <Card className="max-w-2xl p-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <div><Label>Name *</Label><Input value={form.name} onChange={upd("name")} className="mt-1.5" data-testid="single-name" /></div>
        <div><Label>Email *</Label><Input type="email" value={form.email} onChange={upd("email")} className="mt-1.5" data-testid="single-email" /></div>
        <div><Label>Mobile</Label><Input value={form.mobile} onChange={upd("mobile")} className="mt-1.5" data-testid="single-mobile" /></div>
        <div><Label>Employee code</Label><Input value={form.employee_code} onChange={upd("employee_code")} className="mt-1.5" data-testid="single-code" /></div>
        <div>
          <Label>Department</Label>
          <Select value={form.department_id} onValueChange={sel("department_id")}>
            <SelectTrigger className="mt-1.5" data-testid="single-department"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{depts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Designation</Label>
          <Select value={form.designation_id} onValueChange={sel("designation_id")}>
            <SelectTrigger className="mt-1.5" data-testid="single-designation"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{desigs.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Location</Label>
          <Select value={form.location_id} onValueChange={sel("location_id")}>
            <SelectTrigger className="mt-1.5" data-testid="single-location"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{locs.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Date of joining</Label><div className="mt-1.5"><DatePicker value={form.date_of_joining} onChange={(v) => setForm({ ...form, date_of_joining: v })} clearable testid="single-doj" /></div></div>
        <div>
          <Label>Statutory mode</Label>
          <Select value={form.statutory_mode} onValueChange={sel("statutory_mode")}>
            <SelectTrigger className="mt-1.5" data-testid="single-statutory"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUTORY.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <StateZoneFields value={form} onChange={(v) => setForm({ ...form, ...v })} testPrefix="single" triggerClass="mt-1.5" />
      </div>
      <Button className="mt-6" disabled={!form.name || !form.email || invite.isPending} onClick={() => invite.mutate()} data-testid="single-invite-submit">
        <UserPlus className="mr-2 h-4 w-4" /> {invite.isPending ? "Inviting…" : "Send invite"}
      </Button>
      <LinkDialog open={!!link} onClose={() => setLink("")} title="Invitation link"
        note="Share this link with the employee if the email didn't arrive." link={link} />
    </Card>
  );
}

function BulkInvite() {
  const fileRef = useRef();
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);

  const downloadTemplate = async () => {
    const res = await api.get("/org/employees/invite/template", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "employee_invite_template.xlsx"; a.click();
    URL.revokeObjectURL(url);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/org/employees/invite/bulk/preview", fd);
      setPreview(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
    e.target.value = "";
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const { data } = await api.post("/org/employees/invite/bulk/commit", { rows: preview.rows });
      toast.success(`${data.created} employee(s) invited${data.updated ? `, ${data.updated} updated` : ""}`);
      if (data.errors?.length) downloadErrors(data.errors);
      setPreview(null);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setCommitting(false);
    }
  };

  const downloadErrors = (rows) => {
    const header = ["row_number", "name", "email", "status", "reason"];
    const csv = [header.join(",")].concat(
      rows.map((r) => header.map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(","))
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "invite_errors.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      <Card className="flex flex-wrap items-center gap-3 p-5">
        <Button variant="outline" onClick={downloadTemplate} data-testid="download-template"><Download className="mr-2 h-4 w-4" /> Download template</Button>
        <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={onFile} data-testid="bulk-file-input" />
        <Button onClick={() => fileRef.current?.click()} data-testid="upload-file"><Upload className="mr-2 h-4 w-4" /> Upload .xlsx / .csv</Button>
        <span className="text-sm text-slate-400">Columns: name, email, mobile, employee_code, department, designation, location, date_of_joining, statutory_mode, state, zone (state and zone optional; a blank zone uses the zone covering the state). Emails already in the system are updated (blank cells keep the current value, no new invite email).</span>
      </Card>

      {preview && (
        <Card className="p-0">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 p-4">
            <div className="flex flex-wrap gap-2 text-sm">
              <span className="font-medium text-slate-700" data-testid="bulk-counts">{preview.new_count} new · {preview.update_count} to update</span>
              {Object.entries(preview.counts).filter(([k]) => k !== "ok" && k !== "update").map(([k, v]) => (
                <span key={k} className="text-slate-400">· {v} {k.replace(/_/g, " ")}</span>
              ))}
            </div>
            <Button disabled={!preview.valid_count || committing} onClick={commit} data-testid="bulk-commit">
              {committing ? "Committing…" : `Invite ${preview.new_count}, update ${preview.update_count}`}
            </Button>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow><TableHead>#</TableHead><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Status</TableHead><TableHead>Reason</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {preview.rows.map((r, i) => (
                  <TableRow key={i} data-testid={`preview-row-${r.row_number}`}>
                    <TableCell className="text-xs text-slate-400">{r.row_number}</TableCell>
                    <TableCell className="text-sm">{r.name}</TableCell>
                    <TableCell className="text-sm">{r.email}</TableCell>
                    <TableCell><StatusBadge status={r.status} /></TableCell>
                    <TableCell className="text-xs text-slate-500">{r.reason}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {!preview && (
        <Card className="flex flex-col items-center gap-2 py-12 text-center text-slate-400">
          <FileSpreadsheet className="h-8 w-8" />
          <p className="text-sm">Upload a file to preview invites before committing</p>
        </Card>
      )}
    </div>
  );
}
